import CallSplitIcon from '@mui/icons-material/CallSplit';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  Divider,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import moment from 'moment';
import * as React from 'react';

import { SWLoadingButton } from '@/components/LoadingButton';
import { OrderIcon } from '@/components/OrderIcon';
import { SWLoadingButtonOutlined } from '@/components/SWLoadingButtonOutlined';
import useMixPanel from '@/hooks/useMixPanel';
import { Structure } from '@/Structures/types';
import { Unit } from '@/Units/types';
import { naturalCompare } from '@/util';
import { formatPhoneNumberForDisplay } from '@/utils/phoneUtils';

import { ErrorBoundary } from '../../../components/ErrorBoundary';
import { deleteSubscribers, editSubscriber, putSubscribers, updateBillingContact } from '../api';
import { FormDialogSubscriber } from '../form';
import { FormBulkSubscriber } from '../form/bulk';
import { IFormData } from '../form/types';
import { ISubscibersBody, ISubscriber } from '../types';

export interface IUpdateContactRequest {
  new_billing_contact_subscriber_id?: string;
  new_billing_contact_move_in_date?: string;
  old_billing_contact_subscriber_id?: string;
  old_billing_contact_move_out_date?: string;
}

type SortDirection = 'asc' | 'desc';

type SortOrder = -1 | 0 | 1;

type SortableProperty = 'name' | 'start' | 'end' | 'volume' | 'cost' | 'unit_name';

type RowData = {
  id: string;
  unit_name: string;
  unit_id: string;
  email: string;
  first_name: string;
  last_name: string;
  phone_number?: string;
  isBillingContact: boolean;
  moveInDate: string;
  moveOutDate: string;
  hasOpenInvoices: boolean;
  // Single mutually-exclusive status for this tenant relative to the current
  // month's invoice cycle. Replaces the prior two-icon design (check + mail)
  // which was confusing because the two could diverge in non-obvious ways.
  status: InvoiceStatus;
  // True when this tenant's unit has 2+ tenants whose occupancy windows touch
  // the current calendar month. Signals that the next invoice for this unit will
  // be prorated across multiple occupants. Stays on the unit-name cell because
  // it's a property of the unit, not the tenant.
  unitHasProratedSplit: boolean;
};

// Mutually-exclusive per-tenant invoicing status for the CURRENT calendar month.
// One label answers the user's actual question ("what happens with this person?")
// instead of forcing them to reconcile divergent icons. Computed in
// subscribersToRows using the same group-then-pick-winner logic the backend uses.
type InvoiceStatusKind =
  | 'FULL_INVOICE' // winner of their occupancy group, window covers full month
  | 'PRORATED_SHARE' // winner of their group but window covers only part of month
  | 'CO_TENANT' // overlaps an occupancy group but lost the tiebreaker
  | 'DESIGNATED_FUTURE' // is_billing_contact=true but move-in is in the future
  | 'FUTURE_TENANT' // not flagged, move-in is in the future, occupancy on file
  | 'PAST_TENANT' // moved out before the current month started
  | 'NO_OCCUPANCY_INFO'; // no move-in date at all, not flagged, nothing to surface

type InvoiceStatus = {
  kind: InvoiceStatusKind;
  label: string;
  // MUI Chip color prop value. Avoid color-only meaning (Quick Ref §1
  // color-not-only): the label carries the meaning; color reinforces.
  color: 'success' | 'warning' | 'info' | 'default';
  // Per-status tooltip explaining the rule, since the header legend may not be
  // discovered on first visit.
  description: string;
};

const STATUS_DEFS: Record<InvoiceStatusKind, Omit<InvoiceStatus, 'kind'>> = {
  FULL_INVOICE: {
    label: 'Full Invoice',
    color: 'success',
    description:
      'This tenant will receive the next invoice for this unit. Their occupancy covers the full current billing month.',
  },
  PRORATED_SHARE: {
    label: 'Prorated share',
    color: 'info',
    description:
      'This tenant will receive a prorated invoice for the portion of the current month they occupied. Other occupants on this unit will receive their own prorated invoices.',
  },
  CO_TENANT: {
    label: 'Co-tenant (no invoice)',
    color: 'default',
    description:
      'This tenant overlaps with another tenant on this unit who is the designated invoice contact. Only one invoice is generated; the designated contact receives it.',
  },
  DESIGNATED_FUTURE: {
    label: 'Designated, starts later',
    color: 'warning',
    description:
      "Marked as the default invoice contact, but their move-in date is in the future. They won't receive an invoice until they're occupying.",
  },
  FUTURE_TENANT: {
    label: 'Future tenant',
    color: 'default',
    description:
      "Move-in date is in the future. They won't receive an invoice until they're occupying this unit.",
  },
  PAST_TENANT: {
    label: 'Past tenant',
    color: 'default',
    description:
      'Moved out before the current billing month started. Kept on the list for history; will not receive a new invoice.',
  },
  NO_OCCUPANCY_INFO: {
    label: 'No status',
    color: 'default',
    description:
      'No occupancy information on file for the current month. Set a move-in date to start invoicing.',
  },
};

type Comparator = (a: RowData, b: RowData, orderBy?: SortableProperty) => SortOrder;

interface IUnitSubscription {
  subscriber_id: string;
  unit_id: string;
  unit_name: string;
  is_billing_contact: boolean;
  move_in_date: null | string;
  move_out_date: null | string;
  has_open_invoices: boolean;
  open_stripe_invoice_ids: any[];
}

interface ISubscribers {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  phone_number?: string;
  unit_subscriptions: IUnitSubscription[];
}

// Util ///////////////////////////////////////////////////////////////////////

/**
 * Sort in descending order
 *
 * @param {object} a - First object to compare
 * @param {object} b - Second object to compare
 * @param {string} orderBy - Property name to order by
 *
 * @returns {-1 | 0 | 1} - Sort order
 */
const descendingComparator = (a: RowData, b: RowData, orderBy: SortableProperty) => {
  const valA = a[orderBy] ?? '';
  const valB = b[orderBy] ?? '';

  // Use natural alphanumeric sorting for unit_name
  if (orderBy === 'unit_name' && typeof valA === 'string' && typeof valB === 'string') {
    return naturalCompare(valB, valA);
  }

  // $FlowFixMe
  if (valB < valA) {
    return -1;
  }
  // $FlowFixMe
  if (valB > valA) {
    return 1;
  }
  return 0;
};

/**
 * Get sorting function
 *
 * @param {'asc' | 'desc'} order - Direction to sort
 * @param {*} orderBy - name to order by
 *
 * @returns {Function} - Function to sort with
 */
const getComparator = (order: SortDirection, orderBy: SortableProperty): Comparator => {
  return (a, b) => {
    const primary =
      order === 'desc' ? descendingComparator(a, b, orderBy) : -descendingComparator(a, b, orderBy);
    if (primary !== 0) return primary as SortOrder;
    // Tiebreaker: earliest move-in first, regardless of primary sort direction.
    // ISO date strings (YYYY-MM-DD) compare lexically. Missing move-in sorts
    // earliest so unassigned tenants don't trail behind dated ones at random.
    return (a.moveInDate || '').localeCompare(b.moveInDate || '') as SortOrder;
  };
};

/**
 * Sort row data
 *
 * @param {RowData[]} array - Array of RowData
 * @param {Function} comparator - Comparing/sorting function
 *
 * @returns {RowData[]} - Sorted RowData
 */

const hiddenArrow = () => <span />;
const stableSort = (array: RowData[], comparator: Comparator): RowData[] => {
  const stabilizedThis = array.map((el, index) => [el, index]);

  stabilizedThis.sort((a, b) => {
    // $FlowFixMe
    const order = comparator(a[0] as RowData, b[0] as RowData);

    if (order !== 0) return order;

    return (a as number[])[1] - (b as number[])[1];
  });

  return stabilizedThis.map((el) => el[0] as RowData);
};

/**
 * Convert data to table rows
 *
 * @param  {object[]} subscribersArr - Array of Bills
 * @returns {object[]} - Array of rows
 */
const subscribersToRows = (subscribersArr: ISubscribers[]): RowData[] => {
  const rows: RowData[] = [];

  // Use the current calendar month as the next-invoice proxy. The frontend doesn't
  // know real billing-period boundaries from this view; current month is what PMs
  // are usually preparing to bill.
  const monthStart = dayjs().startOf('month');
  const monthEnd = dayjs().endOf('month');

  // Group unit_subscriptions per unit so we can run pick_winner per unit.
  // Carries the parent subscriber_id along with each subscription for keying.
  const subsByUnit: Map<string, { sub: IUnitSubscription; subscriberId: string }[]> = new Map();
  subscribersArr.forEach((subscriber) => {
    subscriber.unit_subscriptions.forEach((unit) => {
      if (!subsByUnit.has(unit.unit_id)) subsByUnit.set(unit.unit_id, []);
      subsByUnit.get(unit.unit_id)!.push({ sub: unit, subscriberId: subscriber.id });
    });
  });

  // Mirror of backend get_valid_billing_contacts (managers/billing_manager.py:677):
  //   1. Filter to subscriptions whose occupancy window overlaps the period.
  //      Null move_in is treated as -infinity ("always occupying"); null move_out
  //      is treated as +infinity. move_out is exclusive (subtract a day for
  //      inclusive comparison).
  //   2. Group overlapping co-tenants per unit by connected windows.
  //   3. Within each group, the is_billing_contact-flagged tenant wins; if none
  //      are flagged, earliest move_in wins.
  // Only the winners qualify for "will receive next invoice". Loser co-tenants
  // (the unflagged half of an overlapping pair) don't appear in this set.
  const winnerKeys = new Set<string>(); // "subscriberId:unitId"
  const unitProratedSplit: Map<string, boolean> = new Map();

  subsByUnit.forEach((entries, unitId) => {
    type WindowedEntry = {
      entry: { sub: IUnitSubscription; subscriberId: string };
      start: dayjs.Dayjs; // inclusive
      end: dayjs.Dayjs; // inclusive
    };

    // Step 1: clip to period & filter to overlapping
    const overlapping: WindowedEntry[] = [];
    entries.forEach((entry) => {
      const movedIn = entry.sub.move_in_date ? dayjs(entry.sub.move_in_date) : null;
      const movedOut = entry.sub.move_out_date ? dayjs(entry.sub.move_out_date) : null;
      const inclusiveEnd = movedOut ? movedOut.subtract(1, 'day') : null;
      const winStart = movedIn ?? monthStart.subtract(100, 'year');
      const winEnd = inclusiveEnd ?? monthEnd.add(100, 'year');
      const clipStart = winStart.isAfter(monthStart) ? winStart : monthStart;
      const clipEnd = winEnd.isBefore(monthEnd) ? winEnd : monthEnd;
      if (!clipEnd.isBefore(clipStart, 'day')) {
        overlapping.push({ entry, start: clipStart, end: clipEnd });
      }
    });

    // Step 2: group connected components (windows overlap if max-start <= min-end)
    const groups: WindowedEntry[][] = [];
    overlapping.forEach((we) => {
      let mergedInto: WindowedEntry[] | null = null;
      for (const g of [...groups]) {
        const overlaps = g.some(
          (other) => !we.end.isBefore(other.start, 'day') && !we.start.isAfter(other.end, 'day'),
        );
        if (overlaps) {
          if (mergedInto === null) {
            g.push(we);
            mergedInto = g;
          } else {
            // bridged two previously-separate groups; merge them
            mergedInto.push(...g);
            const idx = groups.indexOf(g);
            if (idx >= 0) groups.splice(idx, 1);
          }
        }
      }
      if (mergedInto === null) groups.push([we]);
    });

    // Step 3: pick_winner per group
    groups.forEach((group) => {
      if (group.length === 0) return;
      const flagged = group.filter((w) => w.entry.sub.is_billing_contact);
      const candidates = flagged.length > 0 ? flagged : group;
      candidates.sort((a, b) => {
        // null move_in sorts earliest (matches backend's ordinal=1 default)
        const aOrd = a.entry.sub.move_in_date
          ? dayjs(a.entry.sub.move_in_date).valueOf()
          : -Infinity;
        const bOrd = b.entry.sub.move_in_date
          ? dayjs(b.entry.sub.move_in_date).valueOf()
          : -Infinity;
        return aOrd - bOrd;
      });
      const winner = candidates[0];
      winnerKeys.add(`${winner.entry.subscriberId}:${unitId}`);
    });

    // Prorated split icon: unit has 2+ non-overlapping winner groups touching
    // the month → multiple prorated invoices will be generated.
    unitProratedSplit.set(unitId, groups.length >= 2);
  });

  subscribersArr.forEach((subscriber) => {
    subscriber.unit_subscriptions.forEach((unit) => {
      const isWinner = winnerKeys.has(`${subscriber.id}:${unit.unit_id}`);
      const movedIn = unit.move_in_date ? dayjs(unit.move_in_date) : null;
      const movedOut = unit.move_out_date ? dayjs(unit.move_out_date) : null;
      // move_out is exclusive: last occupied day is the day before.
      const inclusiveEnd = movedOut ? movedOut.subtract(1, 'day') : null;

      // Status classification, evaluated in priority order:
      //   1. Past: window ended before this month started.
      //   2. Future: window starts after this month ends.
      //      - With is_billing_contact=true → DESIGNATED_FUTURE (warn the PM
      //        their designated contact won't actually receive the next invoice).
      //      - Without flag → FUTURE_TENANT (occupancy is on file, just not yet).
      //   3. Overlaps this month:
      //      - Winner + covers full month → FULL_INVOICE
      //      - Winner + covers partial month → PRORATED_SHARE (mid-cycle turnover)
      //      - Not winner → CO_TENANT (lost to flagged tenant in same group)
      let statusKind: InvoiceStatusKind;
      if (inclusiveEnd !== null && inclusiveEnd.isBefore(monthStart, 'day')) {
        statusKind = 'PAST_TENANT';
      } else if (movedIn !== null && movedIn.isAfter(monthEnd, 'day')) {
        statusKind = unit.is_billing_contact ? 'DESIGNATED_FUTURE' : 'FUTURE_TENANT';
      } else if (isWinner) {
        const startsBeforeMonth = movedIn === null || !movedIn.isAfter(monthStart, 'day');
        const endsAfterMonth = inclusiveEnd === null || !inclusiveEnd.isBefore(monthEnd, 'day');
        statusKind = startsBeforeMonth && endsAfterMonth ? 'FULL_INVOICE' : 'PRORATED_SHARE';
      } else {
        // Window overlaps the month but they're not the winner of their group.
        // The only way to not be a winner is to be a non-flagged member of an
        // overlapping co-tenant group whose flagged peer took the slot.
        statusKind = movedIn !== null || movedOut !== null ? 'CO_TENANT' : 'NO_OCCUPANCY_INFO';
      }

      const def = STATUS_DEFS[statusKind];
      const row: RowData = {
        id: subscriber.id,
        unit_name: unit.unit_name,
        unit_id: unit.unit_id,
        email: subscriber.email,
        first_name: subscriber.first_name,
        last_name: subscriber.last_name,
        phone_number: subscriber.phone_number,
        isBillingContact: unit.is_billing_contact,
        hasOpenInvoices: unit.has_open_invoices,
        moveInDate: unit.move_in_date || '',
        moveOutDate: unit.move_out_date || '',
        status: { kind: statusKind, ...def },
        unitHasProratedSplit: unitProratedSplit.get(unit.unit_id) || false,
      };

      rows.push(row);
    });
  });

  return rows;
};

// Components /////////////////////////////////////////////////////////////////

// Head ///////////////////////////////

const headCells = [
  { id: 'unit_name', numeric: false, disablePadding: false, label: 'Unit Name' },
  { id: 'email', numeric: false, disablePadding: false, label: 'Email Address' },
  { id: 'phone_number', numeric: false, disablePadding: false, label: 'Phone Number' },
  { id: 'first_name', numeric: false, disablePadding: false, label: 'First Name' },
  { id: 'last_name', numeric: false, disablePadding: false, label: 'Last Name' },
];

type HeadProps = {
  classes: { [x: string]: string };
  order: SortDirection;
  orderBy: SortableProperty;
  onRequestSort: (event: React.MouseEvent, property: SortableProperty) => void;
};

const EnhancedTableHead = ({
  classes: _classes, // destructuring alias to avoid unused variable warning
  order,
  orderBy,
  onRequestSort,
}: HeadProps) => {
  const createSortHandler = (property: SortableProperty) => (event: React.MouseEvent) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        <TableCell align={'justify'}></TableCell>
        {headCells.map((headCell) => (
          <TableCell key={headCell.id} align="center" sortDirection={false}>
            <TableSortLabel
              IconComponent={hiddenArrow}
              onClick={createSortHandler(headCell.id as SortableProperty)}
            >
              {headCell.label}
              {orderBy === headCell.id && (
                <span
                  style={{
                    position: 'absolute',
                    right: '-25px',
                  }}
                >
                  <OrderIcon type={order} />
                </span>
              )}
            </TableSortLabel>
          </TableCell>
        ))}
        <TableCell align={'center'}>
          <Box display="flex" alignItems="center" justifyContent="center">
            Occupancy Dates
            <Tooltip
              title="Adding an occupancy date ensures tenants are billed only for the time they occupy the unit. Without this date, tenants will be charged the full amount."
              placement="top-end"
            >
              <IconButton>
                <InfoOutlinedIcon
                  sx={{
                    fontSize: '17px',
                    color: '#555',
                    ml: 0.5,
                    mb: 0.25,
                  }}
                />
              </IconButton>
            </Tooltip>
          </Box>
        </TableCell>
        {/* Status header with legend tooltip. Renamed from "Primary Invoice Contact"
            because under occupancy-driven billing the column answers a richer question
            than just "who's flagged?": it now describes what will happen with this
            tenant relative to the current invoice cycle. */}
        <TableCell align="center">
          <Box display="flex" alignItems="center" justifyContent="center">
            Status
            <Tooltip
              placement="top-end"
              title={
                <Box sx={{ p: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                    What each status means
                  </Typography>
                  {(Object.keys(STATUS_DEFS) as InvoiceStatusKind[]).map((kind) => (
                    <Box key={kind} sx={{ mb: 0.75 }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                        {STATUS_DEFS[kind].label}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block' }}>
                        {STATUS_DEFS[kind].description}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              }
            >
              <IconButton size="small" sx={{ p: 0.5 }}>
                <InfoOutlinedIcon
                  sx={{
                    fontSize: '17px',
                    color: '#555',
                    ml: 0.5,
                    mb: 0.25,
                  }}
                />
              </IconButton>
            </Tooltip>
          </Box>
        </TableCell>
        <TableCell />
      </TableRow>
    </TableHead>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    padding: `${theme.spacing(4)} 0 ${theme.spacing(3)}`,
  },
  paper: {
    marginBottom: theme.spacing(2),
  },
  table: {
    minWidth: 650,
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  },
}));

type Props = {
  subscribers: ISubscribers[];
  isLoading: boolean;
  units: Unit[];
  setNeedLoadData: Function;
  structure: Structure;
};

interface ISelectedSubscibersInfo {
  id: string;
  UnitID: string;
  UnitName: string;
}

export const SubscribersTable = ({
  subscribers,
  isLoading,
  units,
  setNeedLoadData,
  structure,
}: Props): JSX.Element => {
  const [order, setOrder] = React.useState<SortDirection>('asc');
  const [orderBy, setOrderBy] = React.useState<SortableProperty>('unit_name');
  const [page, setPage] = React.useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = React.useState<number>(5);
  const [isAddFormOpen, setIsAddFormOpen] = React.useState<boolean>(false);
  const [isEditFormOpen, setIsEditFormOpen] = React.useState<boolean>(false);
  const [isBulkFormOpen, setIsBulkFormOpen] = React.useState<boolean>(false);
  const [subscriberToEdit, setSubscriberToEdit] = React.useState<IFormData>();
  const [selectedSubscriber, setSelectedSubscriber] = React.useState<RowData>();
  const [selected, setSelected] = React.useState<readonly string[]>([]);
  const [selectedInfo, setSelectedInfo] = React.useState<ISelectedSubscibersInfo[]>([]);
  const [actionLoading, setActionLoading] = React.useState<boolean>(false);
  // Per-row loading state for the inline date-clear X. Keyed by row id + date type
  // so only the clicked row's X shows a spinner; actionLoading is global and would
  // spin every clear button on the page.
  const [clearingDate, setClearingDate] = React.useState<{
    rowId: string;
    type: 'MoveIn' | 'MoveOut';
  } | null>(null);
  const [propertyInfo] = React.useState({ PropertyID: structure.id, PropertyName: structure.name });
  const { trackEvent } = useMixPanel();
  const [searchQuery, setSearchQuery] = React.useState('');
  const [openDelete, setOpenDelete] = React.useState<boolean>(false);
  // Tracks how many tenants the pending delete will affect, so the confirmation
  // dialog can pluralise correctly. Both single- and multi-delete paths share one
  // openDelete flag; without this we'd always read off `selected.length` which is
  // wrong for the row-menu single-delete (operates on one row, not the selection).
  const [pendingDeleteCount, setPendingDeleteCount] = React.useState<number>(1);
  const [openConfirmDate, setOpenConfirmDate] = React.useState<boolean>(false);
  const [typeDate, setTypeDate] = React.useState<'MoveIn' | 'MoveOut'>('MoveOut');
  const [deleteAction, setDeleteAction] = React.useState<() => void>(() => {});
  const [tenantMoveInDate, setTenantMoveInDate] = React.useState<string | null>(null);
  const [tenantMoveOutDate, setTenantMoveOutDate] = React.useState<string | null>(null);
  const [isEmptyDialogOpen, setEmptyDialogOpen] = React.useState(false);
  // Bumped whenever the confirm-date dialog is dismissed without saving (Cancel
  // or click-outside). Drives the `key` on every occupancy DatePicker so they
  // remount and re-read the controlled `value` prop, which clears Ant's stale
  // internal display state after the user clicked a date but didn't confirm.
  // Without this, the picker keeps visually showing the unsaved date and the
  // clear-X disappears because Ant's input doesn't match `row.moveInDate`.
  const [pickerResetKey, setPickerResetKey] = React.useState<number>(0);
  // ID of the row whose invoice-contact promotion is in flight, or null if none.
  // Used to render a small spinner in place of that row's Status chip while the
  // backend call completes. Without this the user clicks "Select Tenant for
  // Invoicing", the menu closes, and nothing visually changes until the data
  // refetch lands a second or two later — easy to misread as a no-op.
  const [promotingContactRowId, setPromotingContactRowId] = React.useState<string | null>(null);

  // Dismiss the confirm-date dialog without saving. Resets all the in-flight
  // pending-change state and forces the affected pickers to remount.
  const cancelDateConfirm = React.useCallback(() => {
    setOpenConfirmDate(false);
    setTenantMoveInDate(null);
    setTenantMoveOutDate(null);
    setSelectedSubscriber(undefined);
    setPickerResetKey((k) => k + 1);
  }, []);

  const dateFormat = 'YYYY-MM-DD';

  const findBillingContactByUnitId = (unitId) => {
    const tenant = subscribers.find((tenant) =>
      tenant.unit_subscriptions.some(
        (subscription) => subscription.unit_id === unitId && subscription.is_billing_contact,
      ),
    );

    return tenant ? tenant : '';
  };

  const isSelected = (id: string) => selected.indexOf(id) !== -1;

  const classes = useStyles();

  const allRows: RowData[] = subscribersToRows(subscribers);

  const rows = React.useMemo(() => {
    if (!searchQuery.trim()) return allRows;
    const q = searchQuery.toLowerCase();
    return allRows.filter(
      (row) =>
        row.first_name.toLowerCase().includes(q) ||
        row.last_name.toLowerCase().includes(q) ||
        `${row.first_name} ${row.last_name}`.toLowerCase().includes(q) ||
        row.email.toLowerCase().includes(q) ||
        row.unit_name.toLowerCase().includes(q),
    );
  }, [allRows, searchQuery]);

  const handleRequestSort = (event: React.MouseEvent, property: SortableProperty) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    // Re-sorting changes which row is "first" — staying on a later page after the
    // sort means the user is staring at a window into the middle of the new order
    // with no obvious anchor. Match the search-query / rows-per-page convention
    // already used in this component and snap back to page 0 so the user always
    // sees the top of the freshly-sorted list.
    setPage(0);
  };

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) =>
    setPage(newPage);

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const [menuAnchorEl, setMenuAnchorEl] = React.useState(null);
  const [menuSelectedId, setMenuSelectedId] = React.useState(null);

  const handleMenuClick = (event, id) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuSelectedId(id);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuSelectedId(null);
  };

  const handleAdd = async (
    body: ISubscibersBody,
    unitInfo: { unit_id: string; unit_name: string },
  ) => {
    await putSubscribers(body);
    await trackEvent('Subscriber Invited', {
      ...propertyInfo,
      UnitID: unitInfo.unit_id,
      UnitName: unitInfo.unit_name,
    });
    setNeedLoadData(true);
  };

  const handleAddBulk = async (body: ISubscibersBody, type: string) => {
    await putSubscribers(body);
    await trackEvent('Subscribers Imported', {
      ...propertyInfo,
      Count: body.subscribers.length,
      FileType: type,
    });
    setNeedLoadData(true);
  };

  const handleEdit = async (
    body: ISubscriber,
    _unitInfo: { unit_id: string; unit_name: string },
  ) => {
    await editSubscriber(body);
    setNeedLoadData(true);
  };

  const handleDeleteSubscriber = async (
    id: string,
    unitInfo: { UnitID: string; UnitName: string },
  ) => {
    setActionLoading(true);
    await deleteSubscribers({ subscriber_ids: [id] });
    await trackEvent('Subscriber Deleted', { ...propertyInfo, ...unitInfo });
    setNeedLoadData(true);
    setActionLoading(false);
    setOpenDelete(false);
  };

  const handleDeleteSubscribers = async () => {
    setActionLoading(true);
    await deleteSubscribers({ subscriber_ids: selected });
    await trackEvent('Subscribers Deleted', { ...propertyInfo, ...selectedInfo });
    setSelected([]);
    setSelectedInfo([]);
    setNeedLoadData(true);
    setActionLoading(false);
    setOpenDelete(false);
  };

  // Toggle the invoice-contact role to this tenant. Pure flag flip on the backend:
  // the old contact's is_billing_contact is unset and this tenant's is set, with no
  // date changes. Occupancy dates are edited separately via the row's date pickers.
  const handleDialogBillingContact = async (row: RowData) => {
    handleMenuClose();
    if (!row?.id || !row?.unit_id) return;
    const bTenant = findBillingContactByUnitId(row.unit_id);
    setActionLoading(true);
    setPromotingContactRowId(row.id);
    try {
      const request: IUpdateContactRequest = {
        new_billing_contact_subscriber_id: row.id,
      };
      if (bTenant !== '' && bTenant.id) {
        request.old_billing_contact_subscriber_id = bTenant.id;
      }
      await updateBillingContact(request, row.unit_id);
      setNeedLoadData(true);
    } finally {
      setActionLoading(false);
      setPromotingContactRowId(null);
    }
  };

  // Update a tenant's occupancy date (move_in_date or move_out_date) WITHOUT
  // touching the unit's invoice-contact assignment. The /billingContact endpoint
  // is reserved for the transition flow (one tenant displacing another as the
  // unit's invoice contact) and rejects date-only edits when an active billing
  // contact already exists. Use PUT /subscriber/{id} with a partial
  // unit_subscriptions body so the backend only touches the date field.
  const handleMoveOut = async () => {
    if (!selectedSubscriber?.id || !selectedSubscriber?.unit_id) {
      setOpenConfirmDate(false);
      return;
    }
    setActionLoading(true);
    try {
      const dateField =
        typeDate === 'MoveIn'
          ? { move_in_date: tenantMoveInDate }
          : { move_out_date: tenantMoveOutDate };
      const body = {
        id: selectedSubscriber.id,
        unit_subscriptions: [
          {
            unit_id: selectedSubscriber.unit_id,
            ...dateField,
          },
        ],
      };
      await editSubscriber(body);
    } finally {
      setTenantMoveInDate(null);
      setTenantMoveOutDate(null);
      setSelected([]);
      setNeedLoadData(true);
      handleMenuClose();
      setActionLoading(false);
      setSelectedSubscriber(undefined);
      setOpenConfirmDate(false);
    }
  };

  // Clear a tenant's move-in or move-out date. Reversible action so we skip the
  // confirm dialog and apply directly. Backend's map_update_to_db_unit_subscription
  // writes null when the key is present in the body with a null value.
  const handleClearDate = async (row: RowData, type: 'MoveIn' | 'MoveOut') => {
    if (!row?.id || !row?.unit_id) return;
    setActionLoading(true);
    setClearingDate({ rowId: row.id, type });
    try {
      const body = {
        id: row.id,
        unit_subscriptions: [
          {
            unit_id: row.unit_id,
            ...(type === 'MoveIn' ? { move_in_date: null } : { move_out_date: null }),
          },
        ],
      };
      await editSubscriber(body);
    } finally {
      setNeedLoadData(true);
      setActionLoading(false);
      setClearingDate(null);
    }
  };

  const handleOpenEdit = (id: string) => {
    const row = rows.find((i) => i.id === id);
    if (row) {
      const formatData: IFormData = {
        id: row.id,
        email: row.email,
        fist_name: row.first_name,
        last_name: row.last_name,
        phone_number: row.phone_number,
        unit: { id: row.unit_id, label: row.unit_name },
      };
      setSubscriberToEdit(formatData);
      setIsEditFormOpen(true);
    }
  };

  const handleClickItem = (row: RowData) => {
    const item = selected.find((i) => i === row.id);
    const itemInfo = selectedInfo.find((i) => i.id === row.id);
    if (item === undefined) setSelected([...selected, row.id]);
    if (item) {
      const newSelected = selected.filter((item) => item !== row.id);
      setSelected(newSelected);
    }

    if (itemInfo === undefined)
      setSelectedInfo([
        ...selectedInfo,
        { id: row.id, UnitID: row.unit_id, UnitName: row.unit_name },
      ]);
    if (itemInfo) {
      const newSelected = selectedInfo.filter((item) => item.id !== row.id);
      setSelectedInfo(newSelected);
    }
  };

  return (
    <ErrorBoundary>
      <div className={classes.root}>
        <Paper
          variant="outlined"
          sx={{
            borderColor: 'divider',
            borderWidth: 2,
            borderRadius: 2,
            mt: -2,
          }}
          className={classes.paper}
        >
          <Box mx={3} my={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h5" id="tableTitle" component="div" fontWeight={600}>
                Tenants
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  size="small"
                  placeholder="Search by name, email, or unit"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setPage(0);
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ ml: 1 }}>
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ width: 300 }}
                />
                <SWLoadingButton onClick={() => setIsAddFormOpen(true)}>Add Tenant</SWLoadingButton>
                <SWLoadingButton onClick={() => setIsBulkFormOpen(true)}>Import</SWLoadingButton>
              </Stack>
            </Stack>
          </Box>
          <Divider sx={{ borderColor: 'grey', width: '100%' }} />
          <TableContainer>
            <Table className={classes.table} size="medium">
              <EnhancedTableHead
                classes={classes}
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
              />
              <TableBody sx={{ position: 'relative' }}>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} sx={{ py: 10 }} align="center">
                      <Box display="flex" flexDirection="column" alignItems="center" gap={3}>
                        <Typography color="text.secondary">No tenants found.</Typography>
                        <SWLoadingButton onClick={() => setEmptyDialogOpen(true)}>
                          Add your first Tenant
                        </SWLoadingButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  stableSort(rows, getComparator(order, orderBy))
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row) => {
                      // const inputDate = moment(row.moveOutDate, 'YYYY-MM-DD', true);
                      // const today = moment().startOf('day');
                      // const canEdit = row.hasOpenInvoices && row.moveOutDate !== '';
                      // const out =
                      //   !row.hasOpenInvoices && row.moveOutDate !== '' && inputDate.isBefore(today);
                      const isItemSelected = isSelected(row.id);
                      const labelId = `enhanced-table-checkbox-${row.id}`;

                      return (
                        <TableRow
                          hover
                          key={row.id}
                          selected={isItemSelected}
                          aria-checked={isItemSelected}
                          sx={{
                            backgroundColor: 'inherit',
                            opacity: 1,
                          }}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              color="primary"
                              checked={isItemSelected}
                              inputProps={{
                                'aria-labelledby': labelId,
                              }}
                              onClick={() => handleClickItem(row)}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Box
                              display="inline-flex"
                              alignItems="center"
                              justifyContent="center"
                              gap={0.5}
                            >
                              {row.unit_name}
                              {row.unitHasProratedSplit && (
                                <Tooltip
                                  arrow
                                  placement="top"
                                  title="Multiple tenants overlap this month. The next invoice for this unit will be prorated across each occupant's billable days."
                                >
                                  <CallSplitIcon sx={{ fontSize: 16, color: 'rgba(0,0,0,0.55)' }} />
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="center">{row.email}</TableCell>
                          <TableCell align="center">
                            {formatPhoneNumberForDisplay(row.phone_number)}
                          </TableCell>
                          <TableCell align="center">{row.first_name}</TableCell>
                          <TableCell align="center">{row.last_name}</TableCell>
                          <TableCell align="center">
                            <Stack direction={'row'} gap={1} justifyContent="center">
                              {/* Move-in picker with absolute-positioned overlay X.
                                  Ant's native allowClear X is unreliable: it's opacity-0 except
                                  on hover, the hover region is tiny, and Ant's CSS-in-JS
                                  outcompetes MUI sx overrides in this app's bundle order. We
                                  render our own X as a sibling IconButton positioned over the
                                  picker's right edge (where the calendar icon sits), same
                                  visual location as the native X, but a real React click
                                  handler that always renders.
                                  Gating: clear-X only shows when there's a move-in date AND
                                  no move-out date (matching the backend rule that move-in
                                  must precede move-out). */}
                              <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                <DatePicker
                                  key={`movein-${row.id}-${pickerResetKey}`}
                                  placeholder="Move In Date"
                                  format={'MMMM D, YYYY'}
                                  allowClear={false}
                                  value={row.moveInDate ? dayjs(row.moveInDate) : undefined}
                                  disabledDate={(current) =>
                                    !!row.moveOutDate &&
                                    !!current &&
                                    !current.isBefore(dayjs(row.moveOutDate), 'day')
                                  }
                                  onChange={(value, dateString) => {
                                    if (!value || !dateString) {
                                      handleClearDate(row, 'MoveIn');
                                      return;
                                    }
                                    setSelectedSubscriber(row);
                                    setTenantMoveInDate(moment(dateString).format(dateFormat));
                                    setTypeDate('MoveIn');
                                    setOpenConfirmDate(true);
                                  }}
                                />
                                {row.moveInDate && !row.moveOutDate && (
                                  <IconButton
                                    size="small"
                                    aria-label="Clear move-in date"
                                    disabled={
                                      clearingDate?.rowId === row.id &&
                                      clearingDate?.type === 'MoveIn'
                                    }
                                    onMouseDown={(e) => {
                                      // mousedown fires before Ant's input click handler;
                                      // stopPropagation keeps the picker from opening.
                                      e.stopPropagation();
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleClearDate(row, 'MoveIn');
                                    }}
                                    // Sized + positioned to fully cover Ant's calendar suffix
                                    // (which sits at right:11px, ~16px wide). 28px wide white
                                    // box at right:1 leaves no calendar peeking through.
                                    sx={{
                                      position: 'absolute',
                                      top: '50%',
                                      right: 1,
                                      transform: 'translateY(-50%)',
                                      width: 28,
                                      height: 24,
                                      borderRadius: 1,
                                      padding: 0,
                                      backgroundColor: '#fff',
                                      zIndex: 2,
                                      '&:hover': { backgroundColor: '#f5f5f5' },
                                    }}
                                  >
                                    {clearingDate?.rowId === row.id &&
                                    clearingDate?.type === 'MoveIn' ? (
                                      <CircularProgress size={12} />
                                    ) : (
                                      <CloseIcon sx={{ fontSize: 14, color: 'rgba(0,0,0,0.55)' }} />
                                    )}
                                  </IconButton>
                                )}
                              </Box>
                              <Box display="flex" alignItems="center" gap={0.25}>
                                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                  <DatePicker
                                    key={`moveout-${row.id}-${pickerResetKey}`}
                                    placeholder="Move Out Date"
                                    format={'MMMM D, YYYY'}
                                    disabled={!row.moveInDate}
                                    allowClear={false}
                                    value={row.moveOutDate ? dayjs(row.moveOutDate) : undefined}
                                    disabledDate={(current) =>
                                      !!row.moveInDate &&
                                      !!current &&
                                      !current.isAfter(dayjs(row.moveInDate), 'day')
                                    }
                                    onChange={(value, dateString) => {
                                      if (!value || !dateString) {
                                        handleClearDate(row, 'MoveOut');
                                        return;
                                      }
                                      setSelectedSubscriber(row);
                                      setTenantMoveOutDate(moment(dateString).format(dateFormat));
                                      setTypeDate('MoveOut');
                                      setOpenConfirmDate(true);
                                    }}
                                  />
                                  {row.moveOutDate && (
                                    <IconButton
                                      size="small"
                                      aria-label="Clear move-out date"
                                      disabled={
                                        clearingDate?.rowId === row.id &&
                                        clearingDate?.type === 'MoveOut'
                                      }
                                      onMouseDown={(e) => {
                                        e.stopPropagation();
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleClearDate(row, 'MoveOut');
                                      }}
                                      sx={{
                                        position: 'absolute',
                                        top: '50%',
                                        right: 1,
                                        transform: 'translateY(-50%)',
                                        width: 28,
                                        height: 24,
                                        borderRadius: 1,
                                        padding: 0,
                                        backgroundColor: '#fff',
                                        zIndex: 2,
                                        '&:hover': { backgroundColor: '#f5f5f5' },
                                      }}
                                    >
                                      {clearingDate?.rowId === row.id &&
                                      clearingDate?.type === 'MoveOut' ? (
                                        <CircularProgress size={12} />
                                      ) : (
                                        <CloseIcon
                                          sx={{ fontSize: 14, color: 'rgba(0,0,0,0.55)' }}
                                        />
                                      )}
                                    </IconButton>
                                  )}
                                </Box>
                                <Tooltip
                                  arrow
                                  title="Tenant is not billed for usage on the move-out date itself. To avoid a 1-day vacancy gap, set the next tenant's move-in to the same date as this move-out."
                                  placement="top"
                                >
                                  <InfoOutlinedIcon
                                    sx={{ fontSize: 16, color: '#888', cursor: 'help' }}
                                  />
                                </Tooltip>
                              </Box>
                            </Stack>
                          </TableCell>
                          {/* Status column: one mutually-exclusive Chip per row. Replaces the
                              prior check + mail badge combo. Text carries the meaning;
                              color reinforces but doesn't carry it alone (Quick Ref §1
                              color-not-only). Per-chip tooltip restates the rule in case
                              the header legend wasn't read. */}
                          <TableCell align="center">
                            {promotingContactRowId === row.id ? (
                              <CircularProgress size={16} />
                            ) : (
                              <Tooltip arrow placement="top" title={row.status.description}>
                                <Chip
                                  label={row.status.label}
                                  size="small"
                                  color={row.status.color}
                                  variant={row.status.color === 'default' ? 'outlined' : 'filled'}
                                  sx={{
                                    fontWeight: 500,
                                    ...(row.status.kind === 'PAST_TENANT' && {
                                      fontStyle: 'italic',
                                    }),
                                  }}
                                />
                              </Tooltip>
                            )}
                          </TableCell>
                          <TableCell>
                            <IconButton
                              aria-controls={`menu-${row.id}`}
                              aria-haspopup="true"
                              onClick={(event) => handleMenuClick(event, row.id)}
                            >
                              <MoreVertIcon />
                            </IconButton>
                            <Menu
                              id={`menu-${row.id}`}
                              anchorEl={menuAnchorEl}
                              open={Boolean(menuAnchorEl) && menuSelectedId === row.id}
                              onClose={handleMenuClose}
                            >
                              {actionLoading ? (
                                <MenuItem>Loading ...</MenuItem>
                              ) : selected.length > 1 ? (
                                <MenuItem
                                  onClick={() => {
                                    setDeleteAction(() => handleDeleteSubscribers);
                                    setPendingDeleteCount(selected.length);
                                    setOpenDelete(true);
                                    handleMenuClose();
                                  }}
                                >
                                  Delete Tenants
                                </MenuItem>
                              ) : (
                                <>
                                  {!row.isBillingContact && !row.moveOutDate && (
                                    <MenuItem
                                      onClick={() => {
                                        handleDialogBillingContact(row);
                                      }}
                                    >
                                      Select Tenant for Invoicing
                                    </MenuItem>
                                  )}
                                  <MenuItem
                                    onClick={() => {
                                      handleOpenEdit(row.id);
                                      handleMenuClose();
                                    }}
                                    // disabled={canEdit}
                                  >
                                    Edit Tenant
                                  </MenuItem>
                                  <MenuItem
                                    onClick={() => {
                                      setDeleteAction(
                                        () => () =>
                                          handleDeleteSubscriber(row.id, {
                                            UnitName: row.unit_name,
                                            UnitID: row.unit_id,
                                          }),
                                      );
                                      setPendingDeleteCount(1);
                                      setOpenDelete(true);
                                      handleMenuClose();
                                    }}
                                    // disabled={canEdit}
                                  >
                                    Delete Tenant
                                  </MenuItem>
                                </>
                              )}
                            </Menu>
                          </TableCell>
                        </TableRow>
                      );
                    })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, { value: rows.length, label: 'All' }]}
            count={rows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onRowsPerPageChange={handleChangeRowsPerPage}
            component={'div'}
            onPageChange={handleChangePage}
          />
        </Paper>
        <FormDialogSubscriber
          open={isAddFormOpen}
          onClose={() => setIsAddFormOpen(false)}
          type="ADD"
          onSubmit={(body, unitInfo) => handleAdd(body, unitInfo)}
          units={units}
          addTenant={setIsAddFormOpen}
        />

        <FormDialogSubscriber
          open={isEditFormOpen}
          onClose={() => setIsEditFormOpen(false)}
          type="EDIT"
          onSubmit={(body, unitInfo) => handleEdit(body, unitInfo)}
          units={units}
          editData={subscriberToEdit}
          addTenant={setIsAddFormOpen}
        />
        <FormBulkSubscriber
          onClose={() => setIsBulkFormOpen(false)}
          open={isBulkFormOpen}
          units={units}
          onSubmit={(body, type) => handleAddBulk(body, type)}
        />
      </div>

      <Dialog
        open={openDelete}
        onClose={() => setOpenDelete(false)}
        keepMounted
        aria-describedby="alert-dialog-slide-description"
        sx={{ textAlign: 'center' }}
      >
        <Stack justifyContent={'center'} margin={5} gap={2}>
          <img
            src="/img/deleteIcon.png"
            height={56}
            width={56}
            style={{ margin: 'auto' }}
            alt="timeLogo"
          />
          <Typography fontSize={24} fontWeight={700} color={'#DC3545'}>
            {pendingDeleteCount > 1 ? `Delete ${pendingDeleteCount} Tenants` : 'Delete Tenant'}
          </Typography>
          <Typography fontSize={16} fontWeight={400}>
            {pendingDeleteCount > 1
              ? `Are you sure you want to delete these ${pendingDeleteCount} tenants? This will permanently delete the tenants and all associated information.`
              : 'Are you sure you want to delete this tenant? This will permanently delete the tenant and all associated information.'}
          </Typography>
        </Stack>

        <Stack direction={'row'} justifyContent={'center'} margin={1} gap={1}>
          <SWLoadingButtonOutlined onClick={() => setOpenDelete(false)}>
            Cancel
          </SWLoadingButtonOutlined>
          <SWLoadingButton loading={actionLoading} color="error" onClick={deleteAction}>
            Delete
          </SWLoadingButton>
        </Stack>
      </Dialog>
      {/* ADD FIRST TENANT POP-UP */}
      <Dialog
        open={isEmptyDialogOpen}
        onClose={() => setEmptyDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <Box sx={{ position: 'relative', p: 3 }}>
          {/* X in top right */}
          <IconButton
            size="small"
            onClick={() => setEmptyDialogOpen(false)}
            sx={{ position: 'absolute', top: 8, right: 8 }}
          >
            <CloseIcon />
          </IconButton>

          <Typography variant="h6" mb={5}>
            How would you like to add your first tenant?
          </Typography>

          <Stack spacing={2}>
            <SWLoadingButton
              fullWidth
              onClick={() => {
                setEmptyDialogOpen(false);
                setIsAddFormOpen(true);
              }}
            >
              Add Tenant
            </SWLoadingButton>

            <SWLoadingButton
              fullWidth
              onClick={() => {
                setEmptyDialogOpen(false);
                setIsBulkFormOpen(true);
              }}
            >
              Import Spreadsheet
            </SWLoadingButton>

            <SWLoadingButtonOutlined
              fullWidth
              variant="text"
              onClick={() => setEmptyDialogOpen(false)}
            >
              Cancel
            </SWLoadingButtonOutlined>
          </Stack>
        </Box>
      </Dialog>

      <Dialog
        open={openConfirmDate}
        onClose={cancelDateConfirm}
        keepMounted
        aria-describedby="confirm-date-description"
        sx={{ textAlign: 'center' }}
        PaperProps={{ sx: { position: 'relative' } }}
      >
        <IconButton
          aria-label="Close"
          onClick={cancelDateConfirm}
          size="small"
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
        <Stack justifyContent={'center'} margin={5} gap={2}>
          <Typography fontSize={24} fontWeight={700} color={'black'}>
            Confirm {typeDate === 'MoveIn' ? 'Move In' : 'Move Out'} date change
          </Typography>
          <Typography id="confirm-date-description" fontSize={16} fontWeight={400}>
            {(() => {
              const oldDate =
                typeDate === 'MoveIn'
                  ? selectedSubscriber?.moveInDate
                  : selectedSubscriber?.moveOutDate;
              const pendingDate = typeDate === 'MoveIn' ? tenantMoveInDate : tenantMoveOutDate;
              // Bare YYYY-MM-DD has no tz ambiguity, so moment formats it as
              // the property-local calendar day without conversion. Display
              // format matches the DatePicker so the value the PM picked
              // reads identically here.
              const fmt = (d: string | null | undefined) =>
                d ? moment(d).format('MMMM D, YYYY') : null;
              const dateLabel = typeDate === 'MoveIn' ? 'Move In' : 'Move Out';
              return oldDate ? (
                <>
                  Change {dateLabel} date from <strong>{fmt(oldDate)}</strong> to{' '}
                  <strong>{fmt(pendingDate)}</strong>?
                </>
              ) : (
                <>
                  Set {dateLabel} date to <strong>{fmt(pendingDate)}</strong>?
                </>
              );
            })()}
          </Typography>
          <Typography fontSize={13} color="text.secondary">
            Occupancy dates drive invoice proration, so this may change how upcoming bills are split
            for this unit.
          </Typography>
        </Stack>

        <Stack direction={'row'} justifyContent={'center'} margin={1} gap={1}>
          <SWLoadingButtonOutlined onClick={cancelDateConfirm}>Cancel</SWLoadingButtonOutlined>
          <SWLoadingButton loading={actionLoading} onClick={handleMoveOut}>
            Save {typeDate === 'MoveIn' ? 'Move In' : 'Move Out'} date
          </SWLoadingButton>
        </Stack>
      </Dialog>
    </ErrorBoundary>
  );
};
